/**
 * FXC-9009 P6 — WebGPU client-solve → run-node persist payload assembly.
 *
 * A WebGPU solve is computed in the browser. To make its point join the ADB grid /
 * Tailor / lineage like a RANS run, the surface POSTs the already-computed coefficients
 * to `POST /highlift/webgpu-run`, which writes a run node reusing the SAME rans_provenance
 * flat axes + run_path scheme as the RANS `/solve` path (see thread-api highlift.py).
 *
 * This module is the pure assembly of that `WebGpuRunRequest` — the `design`/`operation`
 * records are built EXACTLY as `runSolve` builds them for `/solve` (so the server flattens
 * to identical axes and the WebGPU run dedupes onto / sits beside its RANS counterpart),
 * plus the persist-eligibility rule (explicit cold solve only; never drag-resolve previews).
 * Extracted so it can be unit-tested without rendering the heavy surface.
 */
import type { HighLiftAirfoil } from "../geometry";
import { sectionHingeXc } from "../geometry/slottedSection";
import type { SectionSeed } from "../foil/sectionSeed";
import type { WebGpuRunRequest, FlowMesh } from "../../../api/highlift";
import type { WebGpuSolveResult } from "./backend";

/** The solve outputs the persist needs. CL is required (the lift the grid plots); the rest
 *  are coerced to null when absent. A full `WebGpuSolveResult` satisfies this (CD is required
 *  there; CM/L_over_D optional), and a partial result is tolerated for the persist. */
export type WebGpuSolveCoefficients = Pick<WebGpuSolveResult, "CL"> &
  Partial<Pick<WebGpuSolveResult, "CD" | "CM" | "L_over_D">>;

/** The `design` payload `/solve` (and `/webgpu-run`) take — mirror of the surface's
 *  `designPayload`. Kept here so the WebGPU persist flattens to the SAME design axes. */
export function webgpuDesignPayload(config: HighLiftAirfoil): Record<string, unknown> {
  const { main, design, propeller } = config;
  return {
    main: { chord: main.chord, bluntThickness: main.bluntThickness },
    design,
    propeller,
  };
}

/** The buffer-lineage rule, identical to `runSolve`: tag the run with the COMMITTED
 *  variant slug only when the live buffer still matches its saved baseline (clean). A
 *  dirty/uncommitted buffer keys on its own design_hash and is not linked to a variant. */
export function webgpuLineageSlug(args: {
  isDirty: boolean;
  baselineSource: string;
  baselineSlug: string | null | undefined;
}): string | null {
  const { isDirty, baselineSource, baselineSlug } = args;
  if (!isDirty && (baselineSource === "loaded" || baselineSource === "committed")) {
    return baselineSlug ?? null;
  }
  return null;
}

// --- cs_* provenance axes (aircraft control-surface fold) ---------------------
//
// When the live high-lift design was SEEDED from an aircraft CS cut section (the
// `foil/sectionSeed.ts` handshake), each persisted in-browser run additively carries
// the sparse-wide `cs_*` provenance columns so the ADB/results table can group runs
// by the control they came from. When the design carries NO seed, the record is
// ABSENT entirely — existing rows and payloads are byte-identical.

/** The sparse-wide `cs_*` provenance axes. Params the seed does not carry are null
 *  (honest-absent, never fabricated): the exact cut rig (eta band, gap/fillet, yh)
 *  requires the publisher to attach `SlottedSectionRig` provenance to the section. */
export interface CsRunAxes {
  /** Lifting surface the seeding control was cut from ('wing' | 'htail' | 'vtail' | 'canard'). */
  cs_surface: string | null;
  /** Human name of the seeding control (e.g. 'aileron L'). */
  cs_name: string | null;
  /** Inboard span fraction of the control. */
  cs_eta1: number | null;
  /** Outboard span fraction of the control. */
  cs_eta2: number | null;
  /** Hinge chordwise fraction (x/c) of the cut. */
  cs_xc: number | null;
  /** Slot gap in mm of the source geometry. */
  cs_gap_mm: number | null;
  /** Cove fillet radius in mm of the source geometry. */
  cs_fillet_mm: number | null;
  /** Hinge height fraction between lower and upper surfaces. */
  cs_yh: number | null;
  /** Section deflection at the cut (deg, TE-down positive). */
  cs_deflection_deg: number | null;
}

/** A finite number, else null (the cs axes never carry NaN/Infinity onto the wire). */
function finiteOrNull(v: number | null | undefined): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Build the `cs_*` provenance record from a consumed section seed, or `null` when the
 *  active design carries no seed. `cs_xc` prefers the publisher's exact rig value and
 *  falls back to the hinge fraction recovered from the section's own contours. */
export function csRunAxes(seed: SectionSeed | null | undefined): CsRunAxes | null {
  if (!seed || !seed.section) return null;
  const s = seed.section;
  const rig = s.rig;
  return {
    cs_surface: typeof s.surface === "string" ? s.surface : null,
    cs_name: typeof s.name === "string" ? s.name : null,
    cs_eta1: finiteOrNull(rig?.eta1),
    cs_eta2: finiteOrNull(rig?.eta2),
    cs_xc: finiteOrNull(rig?.xc) ?? sectionHingeXc(s),
    cs_gap_mm: finiteOrNull(rig?.gapMm),
    cs_fillet_mm: finiteOrNull(rig?.filletMm),
    cs_yh: finiteOrNull(rig?.yh),
    cs_deflection_deg: finiteOrNull(s.deflectionDeg),
  };
}

/** `WebGpuRunRequest` + the additive `cs_axes` provenance record.
 *
 *  TODO(cs-fold, backend): `POST /highlift/webgpu-run` (thread-api highlift.py) currently
 *  IGNORES `cs_axes` (Pydantic drops unknown fields), so sending it is harmless but the
 *  columns do not reach the run node yet. The wiring point is `persist_highlift_webgpu_run`:
 *  accept `cs_axes: dict | None` on its `WebGpuRunRequest` model and merge the non-null
 *  entries into the `run_value_json(...)` result before `_insert_node` — value_json is
 *  plain JSON, so no schema migration is needed and seed-less runs stay byte-identical. */
export type WebGpuRunRequestWithCs = WebGpuRunRequest & { cs_axes?: CsRunAxes };

/** Assemble the `WebGpuRunRequest` to persist a successful (cold, explicit) WebGPU solve.
 *  `flow` is the live freestream condition (mach/reynolds); `result` is the in-browser
 *  solve's outputs. The design/operation match `/solve` so the run joins the grid identically. */
export function buildWebGpuRunRequest(args: {
  config: HighLiftAirfoil;
  alphaDeg: number;
  flow: { mach: number; reynolds: number };
  result: WebGpuSolveCoefficients;
  geometrySlug: string | null;
  /** The solved FlowMesh — blob-stored server-side so the field survives reload. */
  flowMesh?: FlowMesh | null;
  /** The CS section seed the active design was created from, if any — adds the `cs_*`
   *  provenance axes. Omit/null (the default) leaves the request byte-identical. */
  csSeed?: SectionSeed | null;
}): WebGpuRunRequestWithCs {
  const { config, alphaDeg, flow, result, geometrySlug, flowMesh, csSeed } = args;
  const csAxes = csRunAxes(csSeed);
  return {
    alpha_deg: alphaDeg,
    mach: flow.mach,
    reynolds: flow.reynolds,
    CL: result.CL,
    CD: result.CD ?? null,
    CM: result.CM ?? null,
    L_over_D: result.L_over_D ?? null,
    design: webgpuDesignPayload(config),
    operation: { ...config.operation },
    geometry_slug: geometrySlug,
    flow_mesh: flowMesh ?? null,
    ...(csAxes ? { cs_axes: csAxes } : {}),
  };
}
